import { getEmojiFlag } from 'countries-list'
import type { CollapsedProjectList } from './hooks/use-collapsible-project-lists/cookie-schema'
import type { MultiSelectOption } from './types'
export { HIGHLIGHT_COLOR_PALETTE } from '@packages/utilities/consts'

export const APP_NAME = 'NLPatent'

export const DATE_FORMAT = 'YYYY-MM-DD'
export const PATENT_REGEX_PATTERNS = [
  /^\D{2,4}\d{5,15}\D{0,2}\d{0,3}$/,
  // Indian patent numbers are a bit different so if it's an indian patent we can be less strict on
  // the format.
  /^IN.{6,}/,
]
export const EMAIL_REGEX = /^\S+@\S+\.\S+$/
export const PASSWORD_REGEX = /^[\w%+.-]+@$/
export const PROJECT_NAME_REGEX = /^[\s\w.:-]+$/
export const MIN_PASSWORD_LENGTH = 6
export const MIN_PROJECT_NAME_LENGTH = 3
export const MAX_UPLOAD_FILE_SIZE = 1024 * 1024 * 128
export const MAX_BODY_SIZE_LIMIT = 1024 * 1024 * 15
export const MIN_DESCRIPTION_LENGTH = 5
export const SHORT_DESCRIPTION_LENGTH = 10
export const MAX_DESCRIPTION_LENGTH = 600
export const DEFAULT_PAGE_SIZE = 10
export const RESULTS_WIDTH_DEFAULT = 0.6

export const ENGINE_SEARCH_LABEL = 'AI Search'
export const PARTNER_SEARCH_LABEL = 'AI + Human Search'

export const LS_REMEMBERED_USER_EMAIL = 'remembered-user-email'
export const LS_VIEWED_PATENTS = 'viewed-patents'
export const LS_THEME = 'theme'

export const GRID = {
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 1400,
}

export const SPLIT_BREAKPOINT = 1000

export const CPC_SECTIONS = [
  {
    value: 'A',
    label: 'A | Human Necessities',
    description:
      'This includes agriculture; hunting and fishing; foodstuffs; tobacco and smoking devices; wearing apparel and accessories; furniture and household articles; medical sciences; medical devices; lifesaving and firefighting; sports, games, and entertainment.',
  },
  {
    value: 'B',
    label: 'B | Operations; Transporting',
    description:
      'This includes separating and mixing; chemical and physical processes; spraying, vibrating; turning; cleaning; waste disposal; metal-working; wood-working; working of clay/stone/cement; working of plastics; power and hand tools; milling; grinding and polishing; paper treatment and paper-making; printing; books and binding; writing articles; packing and storage; lifting and hauling; vehicles; railways; cycles; ships and water vessels; aviation; saddlery and upholstery; nanotechnology.',
  },
  {
    value: 'C',
    label: 'C | Chemistry; Metallurgy',
    description:
      'This includes organic and inorganic chemistry (compounds, compositions and processes); treatment of water; glass; cement, concrete and other stones; fertilizers; explosives; dyes and paints, polishes, resins and adhesives; petroleum, gas or coke industries; oils and fats; biochemistry and genetic engineering, microorganisms;  enzymes; saccharides; fermentation; beer, wine and spirits; metallurgy and alloys; electrolytic or electrophoretic processes and apparatuses; crystals.',
  },
  {
    value: 'D',
    label: 'D | Textiles; Paper',
    description:
      'This includes threads, yarn, ropes and other fibers; wrapping, spinning and twisting; weaving; sewing; treatment of textiles; paper-making; wall and floor materials; laundry.',
  },
  {
    value: 'E',
    label: 'E | Fixed Constructions',
    description:
      'This includes construction of roads, railways, bridges and platforms; hydraulic engineering; excavating; sewage and plumbing; building constructions; structures built for particular purposes (e.g., swimming); scaffolding; locks, keys and door fittings; drilling and mining.',
  },
  {
    value: 'F',
    label: 'F | Mechanical Engineering',
    description:
      'This includes engines and pumps; alternative energy; insulation; fastening or securing constructional elements; shafts; clutches and brakes; springs; belts, cables and ropes; gearing; pistons; pipes; safety devices; lighting; steam, burners and combustion; heating and ventilation; air-conditioning; refrigerators; furnaces; weapons, armour, and ammunition.',
  },
  {
    value: 'G',
    label: 'G | Physics',
    description:
      'This includes information storage; software; computing; data processing; image or video recognition; optics; spectacles and contact lenses; measuring, testing and analyzing; geophysics; meteorology; photography and cinematography; X-ray technology; photosensitive materials and processes; holographic processes or apparatuses; clocks and watches; ticket/fare issuing apparatuses; coin operated apparatuses; traffic control systems; educational or accessibility devices; circuits; musical instruments; acoustics; bioinformatics; IoT technology; nuclear physics.',
  },
  {
    value: 'H',
    label: 'H | Electricity',
    description:
      'This includes software; electric communication; transmission of digital information; wireless communication; basic electric elements; cables; conductors; semiconductors; batteries; insulators; magnets; transformers; capacitors; electric switches; electric incandescent lamps; antennas; lasers and radiation; electric circuits; electric motors, electric power generation, modulation, and amplification; coding and decoding; X-ray techniques.',
  },
  {
    value: 'Y',
    label: 'Y | Other',
    description:
      'Includes general tagging of new technological developments; general tagging of cross-sectional technologies spanning over several sections of the ipc; technical subjects covered by former uspc cross-reference art collections [xracs] and digests.',
  },
] as const

export type CPCSection = (typeof CPC_SECTIONS)[number]['value']

export const IP5_URL =
  'https://en.wikipedia.org/wiki/IP5_(intellectual_property_offices)'

export const LEGAL_STATUSES = {
  GRANTED: {
    value: 'granted',
    label: 'Granted',
    statuses: ['granted', 'active_reinstated'],
  },
  PENDING: {
    value: 'pending',
    label: 'Pending',
    statuses: ['pending'],
  },
  EXPIRED: {
    value: 'expired',
    label: 'Expired',
    statuses: [
      'expired',
      'expired_lifetime',
      'expired_fee_related',
      'expired_',
    ],
  },
  WITHDRAWN: {
    value: 'withdrawn',
    label: 'Withdrawn',
    statuses: ['withdrawn', 'revoked'],
  },
  ABANDONED: {
    value: 'abandoned',
    label: 'Abandoned',
    statuses: ['abandoned'],
  },
  CEASED: {
    value: 'ceased',
    label: 'Ceased',
    statuses: ['ceased'],
  },
  MISSING: {
    value: 'unknown',
    label: 'Unknown',
    statuses: [],
  },
} as const

export type LegalStatusMultiSelectOption = MultiSelectOption<
  (typeof LEGAL_STATUSES)[keyof typeof LEGAL_STATUSES]['value']
>

export const PS_INDUSTRIES = [
  { value: 'business_methods', label: 'Business Methods' },
  { value: 'life_sciences', label: 'Life Sciences' },
  { value: 'mechanical', label: 'Mechanical' },
  {
    value: 'computer_information_sciences',
    label: 'Computer & Information Sciences',
  },
  { value: 'electrical_communications', label: 'Electrical Communications' },
  { value: 'physical_sciences', label: 'Physical Sciences' },
  { value: 'biotechnology', label: 'Biotechnology' },
  { value: 'chemical_engineering', label: 'Chemical Engineering' },
  { value: 'other', label: 'Other' },
]

export const PS_JURISDICTIONS = [
  {
    value: 'uspto',
    label: 'USPTO',
    fullName: 'United States Patent and Trademark Office',
  },
  { value: 'epo', label: 'EPO', fullName: 'European Patent Office' },
  {
    value: 'wipo',
    label: 'WIPO',
    fullName: 'World Intellectual Property Organization',
  },
  {
    value: 'kipo',
    label: 'KIPO',
    fullName: 'Korean Intellectual Property Office',
  },
  { value: 'jpo', label: 'JPO', fullName: 'Japan Patent Office' },
  {
    value: 'cnipa',
    label: 'CNIPA',
    fullName: 'China National Intellectual Property Administration',
  },
  {
    value: 'cipo',
    label: 'CIPO',
    fullName: 'Canadian Intellectual Property Office',
  },
  { value: 'worldwide', label: 'Worldwide' },
]

export const AI_PROGRESS: Record<number, string> = {
  1: 'Description',
  2: 'Technology',
  3: 'Prior Art',
  4: 'Prior Art',
}

export const PAT_JURISDICTIONS = [
  { value: 'uspto', label: `USA ${getEmojiFlag('US')}` },
  { value: 'epo', label: `EPO ${getEmojiFlag('EU')}` },
  { value: 'wipo', label: `WIPO ${getEmojiFlag('UN')}` },
  { value: 'kipo', label: `Korea ${getEmojiFlag('KR')}` },
  { value: 'jpo', label: `Japan ${getEmojiFlag('JP')}` },
  { value: 'cnipa', label: `China ${getEmojiFlag('CN')}` },
  { value: 'cipo', label: `Canada ${getEmojiFlag('CA')}` },
]

export const FTO_JURISDICTIONS = [
  {
    option: 1,
    value: ['uspto', 'wipo'],
    label: `USA ${getEmojiFlag('US')} + WIPO ${getEmojiFlag('UN')}`,
    fullName:
      'United States Patent and Trademark Office + World Intellectual Property Organization',
  },
  {
    option: 2,
    value: ['epo', 'wipo'],
    label: `EPO ${getEmojiFlag('EU')} + WIPO ${getEmojiFlag('UN')}`,
    fullName:
      'European Patent Office + World Intellectual Property Organization',
  },
  {
    option: 3,
    value: ['uspto', 'epo', 'wipo'],
    label: `USA ${getEmojiFlag('US')} + EPO ${getEmojiFlag(
      'EU'
    )} + WIPO ${getEmojiFlag('UN')}`,
    fullName:
      'United States Patent and Trademark Office + European Patent Office + World Intellectual Property Organization',
  },
]

export type FTO_JURISDICTION = (typeof FTO_JURISDICTIONS)[number]

export const VS_JURISDICTIONS = [
  {
    option: 1,
    value: ['uspto', 'wipo'],
    label: `USA ${getEmojiFlag('US')} + WIPO ${getEmojiFlag('UN')}`,
    fullName:
      'United States Patent and Trademark Office + World Intellectual Property Organization',
  },
  { option: 2, value: ['worldwide'], label: 'Worldwide' },
]

export type VS_JURISDICTION = (typeof VS_JURISDICTIONS)[number]

export const VS_REPORTING_FORMAT_OPTIONS = [
  {
    value: 'tier_one',
    label: 'Tier 1',
    description:
      'Includes basic bibliographic information of provided references',
  },
  {
    value: 'tier_two',
    label: 'Tier 2',
    description:
      'Includes specific document locations within the references that may be relevant or of interest',
  },
  {
    value: 'tier_three',
    label: 'Tier 3',
    description:
      'Organizes references into categories according to the elements/features of the search request',
  },
  {
    value: 'tier_four',
    label: 'Tier 4',
    description:
      'Provides a chart that maps and correlates the top reference(s) to the core elements/features of the search request',
  },
]

export const NON_PATENT_LITERATURE = [
  { value: 'no', label: 'No non-patent literature search', description: '' },
  {
    value: 'basic',
    label: 'Basic non-patent literature search',
    description: 'A search of publicly available non-patent data resources',
  },
  {
    value: 'extended',
    label: 'Extended non-patent literature search',
    description:
      'Extension of time expended on NPL searching and utilization of additional subscription-only data resources (e.g., industry specific trade journals, manuals, etc.)',
  },
]

export const PA_TABLE_FIELDS = [
  {
    value: 'title',
    label: 'Title',
    manageLabel: 'Title',
    width: 220,
    default: true,
  },
  {
    value: 'figure',
    label: 'Figures',
    manageLabel: 'Figures',
    width: 220,
    center: true,
    default: false,
  },
  {
    value: 'claims',
    label: 'Claim 1',
    manageLabel: 'Claim 1',
    width: 160,
    default: false,
  },
  {
    value: 'abstract',
    label: 'Abstract',
    manageLabel: 'Abstract',
    width: 160,
    default: false,
  },
  {
    value: 'cpc',
    label: 'CPC',
    manageLabel: 'CPC',
    width: 80,
    default: false,
  },
  {
    value: 'priority_date',
    label: 'Priority',
    manageLabel: 'Priority Date',
    width: 120,
    default: true,
  },
  {
    value: 'application_date',
    label: 'Filing',
    manageLabel: 'Filing Date',
    width: 120,
    default: false,
  },
  {
    value: 'publication_date',
    label: 'Publication',
    manageLabel: 'Publication Date',
    width: 120,
    default: false,
  },
  {
    value: 'projected_expiration_date',
    label: 'Expiration',
    manageLabel: 'Expiration Date',
    width: 120,
    default: false,
  },
  {
    value: 'legal_status',
    label: 'Status',
    manageLabel: 'Status',
    width: 120,
    default: false,
  },
  {
    value: 'inventors',
    label: 'Inventors',
    manageLabel: 'Inventors',
    width: 220,
    default: false,
  },
  {
    value: 'applicants',
    label: 'Applicants',
    manageLabel: 'Applicants',
    width: 220,
    default: false,
  },
  {
    value: 'current_assignees',
    label: 'Owners & Assignees',
    manageLabel: 'Owners & Assignees',
    width: 220,
    default: true,
  },
  {
    value: 'is_litigated',
    label: 'Litigated',
    manageLabel: 'Is Litigated',
    width: 80,
    default: false,
  },
  {
    value: 'is_licensed',
    label: 'Licensed',
    manageLabel: 'Is Licensed',
    width: 80,
    default: false,
  },
] as const

export const SUPPORTED_TABLE_FIELDS = new Set(
  PA_TABLE_FIELDS.map(({ value }) => value)
)

export type TableFieldValue = (typeof PA_TABLE_FIELDS)[number]['value']

export const NO_CACHE_HEADERS = {
  'Cache-Control': 'no-cache',
  Pragma: 'no-cache',
  Expires: '0',
} as const

export const ENGINE_SEARCH_API = '/api/search/engine'
export const TPRC_API = '/api/trpc'
export const PS_API = '/api/search/partner'

export const MIN_HIGHLIGHT_LENGTH = 20
export const HIGHLIGHTED_CLASS = 'nl-highlighted-text-saved'
export const HIGHLIGHTED_CLASS_RELEVANCE = 'nl-highlighted-text-relevance'

export const NLPATENT_REGISTRATION_URL = 'https://www.nlpatent.com/#requestDemo'

export const COMPACT_RESULTS_CONSTRAINT = 620
export const MIN_RESULTS_WIDTH = 275
export const MIN_DETAILS_WIDTH = 440
export const RESULTS_WIDTH_STORAGE_KEY = 'resultsSectionWidth'

export const CHECK_FOR_COOKIE_KEY = 'check_for_cookies'
export const LOGIN_PATH = '/login'

export const LICENSED_TOOLTIP = 'Data based on SEC filings.'
export const LITIGATED_TOOLTIP = 'US only. Based on US District Court records.'
export const PUBLICATION_TYPE_TOOLTIP =
  'In addition to the selected publication type the results will also include the "Unknown" and "Informational" publication types.'

export const NO_TOKEN_TO_REFRESH_ERROR = 'No token to refresh'

export const NAVIGATION_SELECT = {
  OVERVIEW: { label: 'Overview', value: 'overview' },
  FIGURES: { label: 'Figures', value: 'figures' },
  CLAIMS: { label: 'Claims', value: 'claims' },
  DESCRIPTION: { label: 'Description', value: 'description' },
  FAMILY: { label: 'Family Members', value: 'family' },
  EXPLANATION: { label: 'Relevance Analysis', value: 'explanation' },
  ASK_NLPATENT: { label: 'Ask NLPatent', value: 'ask-nlpatent' },
  NOTES: { label: 'Notes', value: 'notes' },
  CITATIONS: { label: 'Citations', value: 'citations' },
} as const

export const COLLAPSED_LISTS_KEY = 'collapsed-project-lists'

export const INITIAL_COLLAPSED_PROJECT_LISTS: CollapsedProjectList = {
  monitor: {
    isCollapsed: false,
  },
  visualize: {
    isCollapsed: false,
  },
}

export const GEN_AI_RESTRICTED_FIELDS = new Set([
  'relevance_analysis',
  'ask_nlpatent_answer',
])

export const WATCHLIST_ICON_BREAKPOINT = 500
